import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/stores/hook";
import useRouterData from "@/hooks/api/useRouterData";
import { resolveHomePath } from "./homePath";

const IDENTITY_KEY = "xale_route_identity";

/**
 * Sends the user home when the identity behind the current URL changes.
 *
 * Why this is needed: server-minted routes embed the tenant id
 * (`/<stage>-<tenantId>-<stageId>`) and the whole route table is filtered per
 * role. A URL opened as one user is therefore almost always a 404 for the next
 * one. Logging out and back in as a different tenant/user left the address bar
 * pointing at the old session's route, so the app rendered a hard 404 whose
 * "Go Back Home" link pointed at `/` — itself missing for dashboard-less roles.
 *
 * The stamp lives in sessionStorage, NOT localStorage, on purpose: it records
 * the identity THIS TAB's URL belongs to. With localStorage, a second tab
 * signing in as someone else would silently "bless" this tab's stale URL, and
 * refreshing here would 404 again.
 */
export function useIdentityRouteGuard() {
  const navigate = useNavigate();
  const { tabs, isLoading, isFetching } = useRouterData();
  const user = useAppSelector((state) => state.auth.currentUser?.user);

  const identity = user?.id
    ? `${user.tenantId ?? user.tenant?.id ?? ""}:${user.id}`
    : null;

  // Set when the identity CHANGED in this tab; cleared once we've moved.
  const pendingRef = useRef(false);

  useEffect(() => {
    if (!identity) return;

    let previous: string | null = null;
    try {
      previous = sessionStorage.getItem(IDENTITY_KEY);
      sessionStorage.setItem(IDENTITY_KEY, identity);
    } catch {
      // Private mode / storage disabled — nothing to guard against, bail out
      // rather than redirect on every render.
      return;
    }

    // No stamp yet (fresh tab, or first load after this shipped) → the URL was
    // never claimed by a different session, so leave the user where they are.
    if (!previous || previous === identity) return;

    pendingRef.current = true;
  }, [identity]);

  useEffect(() => {
    if (!pendingRef.current) return;
    // Wait for the NEW identity's route table before picking a landing spot —
    // otherwise we'd redirect to a path from the session we're leaving.
    if (isLoading || isFetching || !tabs) return;

    const home = resolveHomePath(tabs);
    if (!home) return;

    pendingRef.current = false;
    navigate(home, { replace: true });
  }, [tabs, isLoading, isFetching, navigate]);
}
